import type { useCreateStore } from 'leva';
import type { ReactNode } from 'react';
import type { QualityTier } from '~/lib/performanceTiers';

import { lazy, Suspense, useState, useSyncExternalStore } from 'react';
import { hasHardwareWebGL } from '~/lib/hasHardwareWebGL';
import { isBot } from '~/lib/isBot';
import { TierContext } from '~/lib/tierContext';
import { useAdaptiveTier } from '~/lib/useAdaptiveTier';
import { useInteractionReady } from '~/lib/useInteractionReady';
import { useIsClient } from '~/lib/useIsClient';

const emptySubscribe = () => () => {};
const getIsBot = () => isBot();
const getServerFalse = () => false;

const isDebug = 'true' === import.meta.env.VITE_DEBUG;

const TierDebugPanel = lazy(async () => import('~/components/TierDebugPanel'));

type LevaStore = ReturnType<typeof useCreateStore>;

/**
 * Owns the single adaptive-tier state machine for the whole app: it runs the capability probe
 * once and shares the resolved tier with every canvas through context, so the hero and the
 * background scene never spin up two competing probes or two WebGL contexts.
 *
 * The tier controls render whenever `VITE_DEBUG` is set, or whenever a `levaStore` is supplied —
 * the experiment routes pass their scene's store so the tier controls join that one always-on
 * panel (as a collapsed "Performance" folder) instead of floating in a separate global panel.
 *
 * @param props.children - Subtree that reads the shared tier via `useTier`
 * @param props.levaStore - Optional leva store to render the tier controls into; when given, the
 *   panel is shown regardless of `VITE_DEBUG` so visitors can play with the scene's controls
 * @returns The provider wrapping the subtree, plus the tier panel when debug or a store is set
 */
export function TierProvider({ children, levaStore }: { children: ReactNode; levaStore?: LevaStore }) {
  const mounted = useIsClient();
  const ready = useInteractionReady();
  const bot = useSyncExternalStore(emptySubscribe, getIsBot, getServerFalse);
  const hwWebGL = ready ? hasHardwareWebGL() : false;

  const [override, setOverride] = useState<QualityTier | null>(null);
  const state = useAdaptiveTier({ eligible: mounted && ready && !bot && hwWebGL, override });

  const showPanel = mounted && (isDebug || undefined !== levaStore);

  return (
    <TierContext value={state}>
      {children}
      {showPanel && (
        <Suspense fallback={null}>
          <TierDebugPanel
            tier={state.tier}
            capabilityScore={state.capabilityScore}
            measuredFps={state.measuredFps}
            onOverrideChange={setOverride}
            store={levaStore}
          />
        </Suspense>
      )}
    </TierContext>
  );
}
